import type { Capability } from '../../lib/api'

// The vendor record's tabs, their parts, and where each old address lands.
//
// # WHY TABS AND PARTS
//
// The record grew a tab for every answer and ended with thirteen in one row,
// which reads as a filing cabinet; then it became seven sections in a side
// menu with every part of a section stacked in one long scroll (Profile alone
// stacked its details, its contacts and its legal and bank details). Release
// 3.3 (the 7 Oct plan, section 3.3) makes the sections a row of tabs across the
// page, grouped by the question a reader brings: who the vendor is (Profile),
// how bad it is and what is being done (Risk), what it holds (Data), what was
// signed (Contracts), what was said (Messages), how it leaves (Offboarding) and
// what was done to it (History). A tab with parts shows one part at a time,
// chosen in a segmented control, so no tab is longer than a window and a half.
//
// # THE ADDRESS
//
// Each tab and part is an address of its own (Back model B7):
// #/vendor/<id>/<tab> and #/vendor/<id>/<tab>/<part>, the overview being the
// vendor's own address. A tab is a place a reader goes to, so choosing one
// pushes a history entry and Back steps out of it (B5); a part is a view of
// the same tab, so choosing one replaces the entry.
//
// # WHY THE OLD KEYS STILL WORK
//
// Home, the notices, the findings register, My work and the exit board link to
// #/vendor/:id/full/<key>, and people have those addresses in email. Every
// key such an address can hold, a section's or a part's, is an alias: it opens
// the tab that holds it at that part (placeFor), and the page then writes the
// tab's own address in its place, keeping the history entry.
//
// History needs audit.read, so its tab is drawn, and its addresses land, only
// for a reader who holds it.

export type SectionKey = 'overview' | 'profile' | 'risk' | 'data' | 'contracts' | 'messages' | 'exit' | 'history'

export type Part = { key: string; label: string }

// key: the section's key in old addresses and in the code. slug: the tab's
// word in the address. tab: its label on the tab row.
export type Section = { key: SectionKey; slug: string; tab: string; parts: Part[]; needs?: Capability }

export const SECTIONS: Section[] = [
  { key: 'overview', slug: 'overview', tab: 'Overview', parts: [] },
  {
    key: 'profile',
    slug: 'profile',
    tab: 'Profile',
    parts: [
      { key: 'profile', label: 'Details' },
      { key: 'contacts', label: 'Contacts' },
      { key: 'statutory', label: 'Legal and bank details' },
    ],
  },
  {
    key: 'risk',
    slug: 'risk',
    tab: 'Risk',
    parts: [
      { key: 'risk', label: 'Rating' },
      { key: 'assessments', label: 'Assessments' },
      { key: 'findings', label: 'Findings' },
      { key: 'reassessment', label: 'Reassessment' },
    ],
  },
  {
    key: 'data',
    slug: 'data',
    tab: 'Data',
    parts: [
      { key: 'data', label: 'Data' },
      { key: 'exposure', label: 'Exposure' },
    ],
  },
  {
    key: 'contracts',
    slug: 'contracts',
    tab: 'Contracts',
    parts: [
      { key: 'documents', label: 'What the documents say' },
      { key: 'contracts', label: 'Contracts' },
      { key: 'onboarding', label: 'Onboarding' },
      { key: 'approval', label: 'Approval' },
    ],
  },
  { key: 'messages', slug: 'messages', tab: 'Messages', parts: [{ key: 'messages', label: 'Messages' }] },
  { key: 'exit', slug: 'offboarding', tab: 'Offboarding', parts: [{ key: 'offboarding', label: 'Offboarding' }] },
  { key: 'history', slug: 'history', tab: 'History', parts: [{ key: 'history', label: 'History' }], needs: 'audit.read' },
]

export type Place = { section: SectionKey; part: string | null }

// Where an address key lands. A part key opens its section at that part; a
// section key or a tab's word opens the section at its first part; anything
// else, including no key, opens the overview, because a vendor link that goes
// nowhere in particular should land on the summary rather than on an edit form.
export function placeFor(key: string | undefined, allowed: (c: Capability) => boolean): Place {
  const k = (key ?? '').trim()
  for (const s of SECTIONS) {
    if (s.needs && !allowed(s.needs)) continue
    if (s.key === k || s.slug === k) {
      // A tab whose key is also one of its parts (Contracts) opens that part:
      // an old link to /full/contracts meant the contracts list, whatever part
      // comes first in the tab now (the documents lane's "What the documents
      // say" since 3.3).
      const own = s.parts.findIndex((p) => p.key === k)
      return { section: s.key, part: own > 0 ? k : null }
    }
    const idx = s.parts.findIndex((p) => p.key === k)
    if (idx === 0) return { section: s.key, part: null }
    if (idx > 0) return { section: s.key, part: k }
  }
  return { section: 'overview', part: null }
}

export function sectionOf(key: SectionKey): Section {
  return SECTIONS.find((s) => s.key === key) ?? (SECTIONS[0] as Section)
}

// The part on screen: the one named, or the tab's first. Null on the overview,
// which has no parts.
export function partOf(place: Place): string | null {
  const s = sectionOf(place.section)
  if (place.part && s.parts.some((p) => p.key === place.part)) return place.part
  return s.parts[0]?.key ?? null
}

// The tabs a reader is shown: every section, History only with audit.read.
export function tabsFor(allowed: (c: Capability) => boolean): Section[] {
  return SECTIONS.filter((s) => !s.needs || allowed(s.needs))
}

// The address for a place: the overview is the vendor's own address, a tab is
// #/vendor/<id>/<tab>, and a part other than the tab's first is
// #/vendor/<id>/<tab>/<part>.
export function hashFor(vendorId: string, place: Place): string {
  if (place.section === 'overview') return '#/vendor/' + vendorId
  const s = sectionOf(place.section)
  const part = partOf(place)
  const first = s.parts[0]?.key ?? null
  return '#/vendor/' + vendorId + '/' + s.slug + (part && part !== first ? '/' + part : '')
}

// Reads a vendor address back into a place: the record reads its own address
// first (a reload, a link, Back and Forward). Null for an address that is not
// this vendor's record, or names nothing on it.
//   #/vendor/<id>                      the overview
//   #/vendor/<id>/<tab>[/<part>]       a tab, at a part
//   #/vendor/<id>/full/<key>           an old address: the alias (placeFor)
export function placeFromHash(hash: string, vendorId: string, allowed: (c: Capability) => boolean): Place | null {
  const parts = hash.replace(/^#\/?/, '').split('?')[0]?.split('/').filter(Boolean) ?? []
  if (parts[0] !== 'vendor' || parts[1] !== vendorId) return null
  if (parts.length === 2) return { section: 'overview', part: null }
  if (parts[2] === 'full') return placeFor(parts[3], allowed)
  const s = SECTIONS.find((x) => x.slug === parts[2] || x.key === parts[2])
  if (!s || (s.needs && !allowed(s.needs))) return null
  const part = parts[3] && s.parts.some((p) => p.key === parts[3]) ? (parts[3] as string) : null
  return { section: s.key, part: part === s.parts[0]?.key ? null : part }
}

// Whether a vendor-approval row of My work is a stage's sign-off: it opens the
// Contracts tab, where the stages are signed, rather than the Profile, where
// the vendor is admitted. Read as the vendor page reads the address
// (placeFromHash), so the API's /full/approval (release 3.3, V1), its older
// /full/contracts and the tab's own address all say the same; a test of the
// address's last word sent a business owner to a button they do not have when
// the API's word changed (review of V1). Here, beside placeFromHash, so the
// verb on My work's row (lib/status.ts) and the vendor's step read it alike.
export function signsOnApproval(href: string): boolean {
  const id = /^#\/vendor\/([^/?]+)/.exec(href)?.[1]
  return Boolean(id) && placeFromHash(href, id as string, () => true)?.section === 'contracts'
}

// Whether the address names its place in an older way (#/vendor/<id>/full/<key>,
// or the exit section's old key) and should be written again as the tab's own
// address once it has been read.
export function isOldAddress(hash: string, vendorId: string, allowed: (c: Capability) => boolean): boolean {
  const place = placeFromHash(hash, vendorId, allowed)
  return place !== null && hash.split('?')[0] !== hashFor(vendorId, place)
}

// The words on a part's button in the segmented control, which name the part
// itself to a screen reader too.
export function partLabel(part: string): string | undefined {
  for (const s of SECTIONS) {
    const p = s.parts.find((x) => x.key === part)
    if (p) return p.label
  }
  return undefined
}

export function partAnchor(part: string): string {
  return 'vendor-part-' + part
}
